import {
  Stack,
  Group,
  Button,
  Table,
  Text,
  TextInput,
  Card,
  Modal,
  Alert,
  Textarea,
  Menu,
  ActionIcon,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  IconSearch,
  IconAlertCircle,
  IconCheck,
  IconPencil,
  IconTrash,
  IconPower,
  IconChevronDown,
  IconMapPin,
} from '@tabler/icons-react';
import {
  useOrigenesDestinos,
  useCreateOrigenDestino,
  useUpdateOrigenDestino,
  useDeleteOrigenDestino,
  getErrorMessage,
} from '../../api/hooks';
import type {
  CreateOrigenDestinoDto,
  OrigenDestino,
  UpdateOrigenDestinoDto,
} from '../../api/types';
import { CatalogPageHeader } from '../../components/CatalogPageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { ConfirmDialog } from '../../components/ConfirmDialog';

interface FormValues {
  nombre: string;
  direccion: string;
  codigoPostal: string;
  poblacion: string;
  provincia: string;
  pais: string;
  nifCif: string;
}

const initialFormValues: FormValues = {
  nombre: '',
  direccion: '',
  codigoPostal: '',
  poblacion: '',
  provincia: '',
  pais: 'ES',
  nifCif: '',
};

export function OrigenesDestinosList() {
  const { data: origenesDestinos = [], isLoading, error } = useOrigenesDestinos();
  const crear = useCreateOrigenDestino();
  const actualizar = useUpdateOrigenDestino();
  const eliminar = useDeleteOrigenDestino();
  const [opened, { open, close }] = useDisclosure(false);
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<OrigenDestino | null>(null);

  const filtered = origenesDestinos.filter((o) =>
    [o.nombre, o.poblacion, o.provincia, o.nifCif, o.direccion]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(filter.toLowerCase()),
  );

  const form = useForm<FormValues>({
    initialValues: initialFormValues,
    validate: {
      nombre: (v) => (v.trim() ? null : 'Requerido'),
    },
  });

  useEffect(() => {
    if (!opened) return;
    if (editingId) {
      const o = origenesDestinos.find((x) => x.id === editingId);
      if (o) {
        form.setValues({
          nombre: o.nombre ?? '',
          direccion: o.direccion ?? '',
          codigoPostal: o.codigoPostal ?? '',
          poblacion: o.poblacion ?? '',
          provincia: o.provincia ?? '',
          pais: o.pais ?? 'ES',
          nifCif: o.nifCif ?? '',
        });
        form.resetDirty();
      }
    } else {
      form.setValues(initialFormValues);
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editingId]);

  const handleNew = () => {
    setEditingId(null);
    open();
  };

  const handleEdit = (o: OrigenDestino) => {
    setEditingId(o.id);
    open();
  };

  const handleConfirmDelete = () => {
    if (!toDelete) return;
    eliminar.mutate(toDelete.id, {
      onSuccess: () => {
        notifications.show({
          title: 'Origen/Destino eliminado',
          message: `${toDelete.nombre} ya no aparece en el catalogo.`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        setToDelete(null);
      },
      onError: (e: unknown) =>
        notifications.show({
          title: 'Error al eliminar',
          message: getErrorMessage(e),
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        }),
    });
  };

  const handleToggleActivo = (o: OrigenDestino) => {
    actualizar.mutate(
      { id: o.id, data: { activo: !o.activo } },
      {
        onSuccess: () =>
          notifications.show({
            title: o.activo ? 'Origen/Destino desactivado' : 'Origen/Destino reactivado',
            message: o.nombre,
            color: 'green',
            icon: <IconCheck size={18} />,
          }),
        onError: (err: unknown) =>
          notifications.show({
            title: 'Error al cambiar el estado',
            message: getErrorMessage(err),
            color: 'red',
            icon: <IconAlertCircle size={18} />,
          }),
      },
    );
  };

  const handleSubmit = (values: FormValues) => {
    const trim = (s: string): string | undefined => {
      const t = s.trim();
      return t ? t : undefined;
    };
    const basePayload = {
      nombre: values.nombre.trim(),
      direccion: trim(values.direccion),
      codigoPostal: trim(values.codigoPostal),
      poblacion: trim(values.poblacion),
      provincia: trim(values.provincia),
      pais: trim(values.pais)?.toUpperCase() ?? 'ES',
      nifCif: trim(values.nifCif),
    };

    if (editingId) {
      const updateData: UpdateOrigenDestinoDto = basePayload;
      actualizar.mutate(
        { id: editingId, data: updateData },
        {
          onSuccess: () => {
            notifications.show({
              title: 'Origen/Destino actualizado',
              message: `${values.nombre} guardado correctamente.`,
              color: 'green',
              icon: <IconCheck size={18} />,
            });
            close();
          },
          onError: (e: unknown) =>
            notifications.show({
              title: 'Error al actualizar',
              message: getErrorMessage(e),
              color: 'red',
              icon: <IconAlertCircle size={18} />,
            }),
        },
      );
      return;
    }

    const payload: CreateOrigenDestinoDto = basePayload;
    crear.mutate(payload, {
      onSuccess: () => {
        notifications.show({
          title: 'Origen/Destino dado de alta',
          message: `${values.nombre} ya esta disponible.`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        close();
      },
      onError: (e: unknown) =>
        notifications.show({
          title: 'Error al dar de alta',
          message: getErrorMessage(e),
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        }),
    });
  };

  return (
    <Stack gap="md">
      <CatalogPageHeader
        title="Orígenes / Destinos"
        subtitle="Catalogo reutilizable de puntos de origen y destino para los envíos."
        icon={IconMapPin}
        newLabel="Nuevo origen o destino"
        count={origenesDestinos.length}
        onNew={handleNew}
        filters={
          <TextInput
            placeholder="Buscar por nombre, poblacion, provincia o NIF…"
            leftSection={<IconSearch size={16} />}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            w={280}
          />
        }
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          title="No se pudieron cargar los origenes/destinos"
        >
          Verifica tu conexion y vuelve a intentarlo.
        </Alert>
      )}

      <Card withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nombre</Table.Th>
              <Table.Th>Poblacion</Table.Th>
              <Table.Th>Provincia</Table.Th>
              <Table.Th>NIF/CIF</Table.Th>
              <Table.Th>Estado</Table.Th>
              <Table.Th style={{ width: 90, textAlign: 'right' }}>Acciones</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr>
                <Table.Td colSpan={6}>Cargando...</Table.Td>
              </Table.Tr>
            )}
            {!isLoading && !error && filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  {origenesDestinos.length === 0
                    ? 'No hay origenes/destinos. Da de alta el primero con el boton de arriba.'
                    : 'Sin resultados para el filtro.'}
                </Table.Td>
              </Table.Tr>
            )}
            {filtered.map((o) => (
              <Table.Tr key={o.id}>
                <Table.Td>{o.nombre}</Table.Td>
                <Table.Td>{o.poblacion ?? '—'}</Table.Td>
                <Table.Td>{o.provincia ?? '—'}</Table.Td>
                <Table.Td>
                  {o.nifCif ? (
                    <Text size="sm" ff="monospace">
                      {o.nifCif}
                    </Text>
                  ) : (
                    '—'
                  )}
                </Table.Td>
                <Table.Td>
                  <StatusBadge activo={o.activo} />
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Menu position="bottom-end" withinPortal shadow="md">
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray" aria-label="Acciones">
                        <IconChevronDown size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconPencil size={14} />}
                        onClick={() => handleEdit(o)}
                      >
                        Editar
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconPower size={14} />}
                        onClick={() => handleToggleActivo(o)}
                      >
                        {o.activo ? 'Desactivar' : 'Reactivar'}
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={() => setToDelete(o)}
                      >
                        Eliminar
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal
        opened={opened}
        onClose={close}
        title={editingId ? 'Editar origen o destino' : 'Nuevo origen o destino'}
        centered
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Nombre"
              placeholder="Almacen Central Madrid"
              required
              {...form.getInputProps('nombre')}
            />
            <Textarea
              label="Direccion"
              placeholder="Calle, numero, piso..."
              autosize
              minRows={2}
              {...form.getInputProps('direccion')}
            />
            <Group grow>
              <TextInput
                label="Codigo postal"
                placeholder="28013"
                {...form.getInputProps('codigoPostal')}
              />
              <TextInput
                label="Poblacion"
                placeholder="Madrid"
                {...form.getInputProps('poblacion')}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Provincia"
                placeholder="Madrid"
                {...form.getInputProps('provincia')}
              />
              <TextInput
                label="Pais (ISO-2)"
                placeholder="ES"
                maxLength={2}
                {...form.getInputProps('pais')}
              />
            </Group>
            <TextInput label="NIF/CIF" placeholder="B12345678" {...form.getInputProps('nifCif')} />
            <Group justify="flex-end" mt="md">
              <Button
                variant="default"
                onClick={close}
                disabled={crear.isPending || actualizar.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" loading={crear.isPending || actualizar.isPending}>
                {editingId ? 'Guardar cambios' : 'Dar de alta'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <ConfirmDialog
        opened={toDelete !== null}
        title="Eliminar origen o destino"
        message={toDelete ? `¿Eliminar ${toDelete.nombre}? Esta accion no se puede deshacer.` : ''}
        confirmLabel="Eliminar"
        confirmColor="red"
        loading={eliminar.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </Stack>
  );
}
